import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Select, DatePicker, Button, Table, Tag, message, Space, Form, Tooltip,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import ResizableTitle from '../components/ResizableTitle';
import { loadPageCache, savePageCache } from '../utils/pageCache';

import { selectFilter } from '../utils/selectFilter';
const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Draft',             value: 'assigned' },
  { label: 'In Progress',       value: 'in_progress' },
  { label: 'Completed',         value: 'completed' },
  { label: 'Rescore Requested', value: 'rescore_requested' },
  { label: 'Canceled',          value: 'canceled' },
];

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Rescore Requested',
  canceled:          'Canceled',
};

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

function formatDuration(seconds) {
  if (seconds == null) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function _serializeFormValues(f) {
  const v = { ...f };
  if (v.dateRange)        v.dateRange        = v.dateRange.map((d) => d?.toISOString?.() ?? d);
  if (v.evalCreatedRange) v.evalCreatedRange = v.evalCreatedRange.map((d) => d?.toISOString?.() ?? d);
  return v;
}

export default function FeedbackOnlyPage() {
  const navigate    = useNavigate();
  const currentUser = React.useContext(UserContext);

  const _cache = loadPageCache('feedback-only');
  const [cases, setCases]               = useState(_cache?.cases ?? []);
  const [loading, setLoading]           = useState(false);
  const [agentOptions, setAgentOptions]       = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [formOptions, setFormOptions]         = useState([]);
  const [userOptions, setUserOptions]         = useState([]);
  const [colWidths, setColWidths]       = useState({});

  const [form] = Form.useForm();

  useEffect(() => {
    if (_cache?.formValues) {
      const v = { ..._cache.formValues };
      if (v.dateRange)        v.dateRange        = v.dateRange.map((d) => d ? dayjs(d) : null);
      if (v.evalCreatedRange) v.evalCreatedRange = v.evalCreatedRange.map((d) => d ? dayjs(d) : null);
      form.setFieldsValue(v);
    } else {
      form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()], status: ['assigned', 'in_progress', 'rescore_requested'] });
    }
  }, []); // eslint-disable-line

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/agent-stats/options')
      .then((r) => {
        setCampaignOptions(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setAgentOptions(r.data.agents.map((a) => ({ label: a.name, value: a.id ?? a.name })));
      })
      .catch(() => {});
    api.get('/api/eval-forms/published')
      .then((r) => setFormOptions(r.data.map((f) => ({ label: `${f.Name} v${f.Version}`, value: f.ID }))))
      .catch(() => {});
    // Who has actually been assigned an evaluation here, not who could be
    // assigned one today. A search page looks backwards: someone who has
    // left, changed team or lost the permission still has months of work
    // behind them, and the old list dropped them -- silently, since the
    // name was simply absent rather than empty.
    api.get('/api/cases/assigned-to-options', { params: { feedback_only: 1 } })
      .then((r) => setUserOptions((Array.isArray(r.data) ? r.data : []).map((u) => ({
        // Marked rather than hidden: their evaluations are still there.
        label: (u.DisplayName || u.Email) + (u.IsActive ? '' : ' (inactive)'),
        value: u.ID,
      }))))
      .catch(() => {});
  }, []);

  const fetchCases = useCallback(async (f = {}) => {
    setLoading(true);
    try {
      const params = { is_feedback_only: '1' };
      if (f.agent_name != null) {
        if (typeof f.agent_name === 'number') params.agent_user_id = f.agent_name;
        else params.agent_name = f.agent_name;
      }
      if (f.assigned_to_user_id)    params.assigned_to_user_id = f.assigned_to_user_id;
      // '0' is a real choice, so a plain truthiness test would drop it.
      if (f.created_by_ai != null && f.created_by_ai !== '') params.created_by_ai = f.created_by_ai;
      if (f.status?.length)         params.status              = f.status.join(',');
      if (f.campaign?.length)       params.campaign            = f.campaign.join(',');
      if (f.form_id?.length)        params.form_id             = f.form_id.join(',');
      if (f.dateRange?.[0])         params.start_date          = f.dateRange[0].format('YYYY-MM-DD');
      if (f.dateRange?.[1])         params.end_date            = f.dateRange[1].format('YYYY-MM-DD');
      if (f.evalCreatedRange?.[0])  params.eval_created_start  = f.evalCreatedRange[0].format('YYYY-MM-DD');
      if (f.evalCreatedRange?.[1])  params.eval_created_end    = f.evalCreatedRange[1].format('YYYY-MM-DD');

      const res = await api.get('/api/cases', { params });
      setCases(res.data);
      savePageCache('feedback-only', { cases: res.data, formValues: _serializeFormValues(f) });
    } catch {
      message.error('Failed to load feedback-only evaluations.');
    } finally {
      setLoading(false);
    }
  }, []);


  const handleSearch = (values) => fetchCases(values);

  const exportToExcel = () => {
    const rows = cases.map((c) => ({
      'Contact ID':           c.ContactID,
      'Status':               STATUS_LABEL[c.Status] ?? c.Status,
      'Agent':                c.AgentName ?? '',
      'Campaign':             c.Campaign ?? '',
      'Skill':                c.Skill ?? '',
      'Call Date':            c.CallDate?.slice(0, 10) ?? '',
      'Eval Form':            c.FormName ? `${c.FormName}${c.FormVersion ? ` v${c.FormVersion}` : ''}` : '',
      'Assigned To':          c.AssignedToName ?? '',
      'Score':                c.TotalPossible > 0 ? c.TotalEarned : '',
      'Total Pts Possible':   c.TotalPossible > 0 ? c.TotalPossible : '',
      'Duration':             c.AgentTime != null ? `${Math.floor(c.AgentTime / 60)}:${String(c.AgentTime % 60).padStart(2, '0')}` : '',
      'Last Modified':        formatTS(c.UpdatedAt),
      'Created On':           formatTS(c.CreatedAt),
      'Completed On':         formatTS(c.CompletedAt),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback Only');
    XLSX.writeFile(wb, 'feedback-only-evaluations.xlsx');
  };

  const columns = [
    { title: 'Contact ID',  dataIndex: 'ContactID',    key: 'ContactID',    width: 110 },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 160,
      render: (status) => (
        <Tag color={STATUS_COLOR[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Tag>
      ),
    },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 150 },
    {
      title: 'Form', dataIndex: 'FormName', key: 'FormName', ellipsis: true,
      render: (name, record) =>
        name
          ? `${name}${record.FormVersion ? ` v${record.FormVersion}` : ''}`
          : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 110,
      render: (v) => v?.slice(0, 10) ?? '-',
      sorter: (a, b) => (a.CallDate ?? '').localeCompare(b.CallDate ?? ''),
    },
    {
      title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 150,
      render: (name, record) => {
        if (!name) return '—';
        if ((currentUser?.CanViewAgentSummary ?? 0) < 1) return name;
        return (
          <Typography.Link
            onClick={(e) => {
              e.stopPropagation();
              const q = record.AgentNo
                ? `agent_no=${encodeURIComponent(record.AgentNo)}`
                : `agent_name=${encodeURIComponent(name)}`;
              navigate(`/agents?${q}`);
            }}
          >
            {name}
          </Typography.Link>
        );
      },
    },
    { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign' },
    { title: 'Skill',    dataIndex: 'Skill',    key: 'Skill' },
    {
      title: 'Score', dataIndex: 'TotalEarned', key: 'TotalEarned', width: 90,
      sorter: (a, b) => (a.TotalEarned ?? 0) - (b.TotalEarned ?? 0),
      render: (v, record) =>
        record.TotalPossible > 0
          ? `${v} / ${record.TotalPossible}`
          : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Duration', dataIndex: 'AgentTime', key: 'AgentTime', width: 90,
      render: formatDuration,
    },
    {
      title: 'Last Modified', dataIndex: 'UpdatedAt', key: 'UpdatedAt', width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.UpdatedAt ?? '').localeCompare(b.UpdatedAt ?? ''),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Created On', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.CreatedAt ?? '').localeCompare(b.CreatedAt ?? ''),
    },
    {
      title: 'Completed On', dataIndex: 'CompletedAt', key: 'CompletedAt', width: 160,
      render: (v) => formatTS(v),
      sorter: (a, b) => (a.CompletedAt ?? '').localeCompare(b.CompletedAt ?? ''),
    },
  ].map(toResizable);

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        Feedback Only Evaluations
        <Tag color="purple" style={{ marginLeft: 12, verticalAlign: 'middle', fontSize: 13 }}>
          Not counted in official scores
        </Tag>
      </Typography.Title>

      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="agent_name" label="Agent">
          <Select
            allowClear showSearch filterOption={selectFilter} placeholder="All agents"
            options={agentOptions} style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item name="assigned_to_user_id" label="Assigned To">
          <Select
            allowClear showSearch optionFilterProp="label"
            placeholder="All users" options={userOptions} style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item name="created_by_ai" label="Created By">
          <Select
            allowClear
            placeholder="Anyone"
            style={{ width: 150 }}
            options={[
              { label: 'AI', value: '1' },
              { label: 'A person', value: '0' },
            ]}
          />
        </Form.Item>

        <Form.Item name="dateRange" label="Call Date">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="evalCreatedRange" label="Eval Created">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>

        <Form.Item name="status" label="Status">
          <Select
            mode="multiple" allowClear placeholder="All statuses"
            options={STATUS_OPTIONS} maxTagCount="responsive" style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item name="campaign" label="Campaign">
          <Select
            mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All campaigns"
            options={campaignOptions} maxTagCount="responsive" style={{ width: 200 }}
          />
        </Form.Item>

        <Form.Item name="form_id" label="Eval Form">
          <Select
            mode="multiple" allowClear showSearch filterOption={selectFilter} placeholder="All forms"
            options={formOptions} maxTagCount="responsive" style={{ width: 220 }}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>Filter</Button>
            <Button onClick={() => { form.resetFields(); fetchCases({}); }}>Reset</Button>
            <Tooltip title={cases.length === 0 ? 'No data to export' : ''}>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={cases.length === 0}
              >
                Export
              </Button>
            </Tooltip>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="ID"
        dataSource={cases}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          defaultPageSize: 50, showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          showTotal: (t) => `${t} feedback-only evaluations`,
        }}
        onRow={(record) => ({
          onClick: () => navigate(`/cases/${record.ID}`),
          style: { cursor: 'pointer' },
        })}
      />
    </>
  );
}
